/**
 * Which map decor kinds MOVE, and when they are allowed to move.
 *
 * The pack ships Outdoor_Decor_Animations and the map used none of it: mushrooms
 * were baked as a single rest frame and the birch-grove fountain was a still
 * picture of running water. A kind only appears here when the animation is the
 * SAME ART as the still it replaces (or, for the fountain, is scale-compensated
 * back to it) — see scripts/build-decor-anim.py, which measures that. The
 * script bakes the frames;
 * this module is the single place that says which kinds have them, and it is
 * deliberately pure data + pure functions so the rules can be tested — jsdom
 * cannot drive Pixi, so nothing about the actual sprites can be.
 *
 * NOT here: rock/boulder. Their sheets exist but the motion is a 1-2px settle on
 * scenery the map carries by the hundred (see scripts/build-rock-decor.py).
 */
import { TILE } from './world';
import type { Decor } from './world';

export interface DecorAnimSpec {
  /** On-disk basename under /sprites/decor/anim (matches DECOR_FILE, not the kind). */
  file: string;
  /** WHICH variant indices have an animated set — not a count: Fisherman Fin is
   *  oracle variant 1 while the grove oracle (variant 0) has no animation. */
  variants: number[];
  /** Frames per variant. */
  frames: number;
  /** Base playback rate; per-sprite jitter is applied on top (see animFps). */
  fps: number;
}

export const DECOR_ANIM: Record<string, DecorAnimSpec> = {
  // NOT grass_tuft / flowers. The pack's Grass_*_Anim and Flowers_*_Anim are
  // different, much smaller art than the tufts and wildflowers the map draws
  // (grass_0 12x13/101px vs 11x5/22px; the tall flowers lose their stalks), and
  // it ships no animated version of those stills — animating them silently
  // replaced the map's most numerous decor. See scripts/build-decor-anim.py.
  mushrooms: { file: 'mushroom', variants: [0, 1, 2], frames: 6, fps: 3 },
  // Fisherman Fin's idle breath. He is drawn as oracle variant 1 (see
  // scripts/build-fisherman-fin.py); the grove oracle stays a still.
  oracle: { file: 'oracle', variants: [1], frames: 6, fps: 4 },
  // One sprite on the whole map, and it is a water feature — it runs at a
  // believable rate and needs no jitter to hide repetition.
  fountain: { file: 'fountain', variants: [0], frames: 8, fps: 8 },
};

export const isAnimatedDecorKind = (kind: string): boolean => kind in DECOR_ANIM;

/** Does this exact decor entity have an animated set (kind AND variant)? */
export const hasDecorAnim = (d: Pick<Decor, 'kind' | 'variant'>): boolean => {
  const spec = DECOR_ANIM[d.kind];
  return !!spec && spec.variants.includes(d.variant);
};

/** Every animated-decor frame URL, keyed the way textures.ts keys its loads. */
export function decorAnimUrls(): Record<string, string> {
  const u: Record<string, string> = {};
  for (const [kind, spec] of Object.entries(DECOR_ANIM))
    for (const v of spec.variants)
      for (let f = 0; f < spec.frames; f++)
        u[`decoranim_${kind}_${v}_${f}`] = `/sprites/decor/anim/${spec.file}_${v}_${f}.png`;
  return u;
}

/**
 * Start frame and rate for one entity, derived from its TILE — never from
 * Math.random.
 *
 * A field of tufts sharing a frame counter reads as one blinking grid rather
 * than as wind; this is the same positional stagger the water FX use
 * (makeWaterSprite), so scatter that is rebuilt by the culler comes back on the
 * same phase it left on instead of snapping.
 */
export const animStartFrame = (d: Pick<Decor, 'tx' | 'ty'>, frames: number): number =>
  Math.abs(d.tx * 7 + d.ty * 13) % frames;

export const animFps = (d: Pick<Decor, 'tx' | 'ty'>, base: number): number =>
  base * (0.7 + (Math.abs(d.tx * 31 + d.ty * 17) % 7) / 10);

/**
 * The culling rule for everything on the shared ticker.
 *
 * A farm is hundreds of thousands of tiles and the ticker walks every registered
 * entry every frame, so animation is paid for ONLY inside the hero's vision
 * square — exactly what the coastline already did, lifted out of the farm.ts
 * ticker so the decor animation cannot drift from it and so it can be tested.
 * Square, not radial: it matches the fog/vision box the player actually sees and
 * costs two comparisons instead of a hypot per sprite per frame.
 */
export const withinAnimVision = (
  ax: number, ay: number, vx: number, vy: number, visPx: number,
): boolean => Math.abs(ax - vx) <= visPx && Math.abs(ay - vy) <= visPx;

/**
 * Render scale for the fountain's animated art.
 *
 * The still fountain (sprites/decor/fountain_0.png, from the pack's Fountain.png)
 * is 32x28; the animated one is a different fountain in the same set and trims to
 * 32x36. At the shrine's existing 1.6 scale that would suddenly grow the grove
 * landmark by a third of a tile in height. 1.6 * 28/36 keeps it drawn at the size
 * players already know, so the only visible change is that the water moves.
 */
export const FOUNTAIN_ANIM_SCALE = 1.6 * (28 / 36);

/** Vision half-width used for animation culling, in world px. */
export const animVisionPx = (visionTiles: number): number => visionTiles * TILE;
